import React from 'react';
import PropTypes from 'prop-types';
import { FaExclamationTriangle } from 'react-icons/fa';
import { useFlowEnvironment } from '../hooks/useFlowEnvironment';
import EnvironmentDonutCard from './charts/EnvironmentDonutCard';
import { formatHumanDateWindow } from '../shared/analyticsViewUtils';

/**
 * Diagnostic dashboard card visualizing client runtime and hardware telemetry
 * with SVG donut charts, summary counts, and full light theme styling.
 * Scoped to organization and site-collection portfolio.
 */
export default function FlowEnvironmentPanel({
  filters = {},
  title = 'Runtime & hardware telemetry'
}) {
  const { data, loading, error, refetch } = useFlowEnvironment(filters, {
    enabled: Boolean(filters.organizationId && filters.siteCollectionId && (filters.startDate || filters.fromUtc))
  });

  const totalSessions = data?.totalSessions ?? 0;
  const deviceTypes = data?.deviceTypes || [];
  const browsers = data?.browsers || [];
  const operatingSystems = data?.operatingSystems || [];
  const locations = data?.locations || [];

  // Date range formatting
  const startIso = data?.dateRange?.startDate || filters.startDate || filters.fromUtc;
  const endIso = data?.dateRange?.endDate || filters.endDate || filters.toUtc;
  const dateWindowText = formatHumanDateWindow(startIso, endIso);

  // Summary counts string matching: "2 device types · 2 browsers · 2 OS · 3 regions"
  const summaryCounts = [
    `${deviceTypes.length} ${deviceTypes.length === 1 ? 'device type' : 'device types'}`,
    `${browsers.length} ${browsers.length === 1 ? 'browser' : 'browsers'}`,
    `${operatingSystems.length} OS`,
    `${locations.length} ${locations.length === 1 ? 'region' : 'regions'}`
  ].join(' · ');

  return (
    <section
      className="w-full min-w-0 bg-white border border-slate-200/80 rounded-2xl p-6 sm:p-7 shadow-sm flex flex-col gap-6"
      data-testid="flow-environment-panel"
    >
      {/* Header Layout */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 pb-4 border-b border-slate-200">
        {/* Left Side: Title, Scope Pill, Subtitle */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-xl font-bold text-slate-900 tracking-tight m-0">
              {title}
            </h2>
            <span
              className="text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap bg-purple-50 text-purple-700 border border-purple-200"
              title="Aggregated across site collection"
              data-testid="env-scope-pill"
            >
              Site-wide
            </span>
          </div>

          {/* Subtitle with date window and summary counts */}
          <p className="text-xs sm:text-sm text-slate-500 font-normal m-0" data-testid="env-subtitle-meta">
            {dateWindowText} · {summaryCounts}
          </p>
        </div>

        {/* Right Side: Total Sessions Callout */}
        <div className="text-right self-end sm:self-auto flex-shrink-0">
          <div
            className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-none"
            data-testid="env-header-total-sessions"
          >
            {loading ? '—' : totalSessions.toLocaleString()}
          </div>
          <div className="text-xs text-slate-500 font-normal mt-1 leading-none">
            captured sessions
          </div>
        </div>
      </div>

      {/* Error Notice */}
      {error && (
        <div
          className="flex items-center gap-2.5 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-xs"
          role="alert"
          data-testid="env-panel-error"
        >
          <FaExclamationTriangle className="text-red-500 text-base flex-shrink-0" />
          <span className="flex-1 font-medium">{error}</span>
          <button
            type="button"
            className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-2.5 py-1 rounded-md transition-colors cursor-pointer border-none"
            onClick={refetch}
          >
            Retry
          </button>
        </div>
      )}

      {/* 4-Column Grid of Metric Donut Cards */}
      <div className="w-full min-w-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <EnvironmentDonutCard
          title="Device types"
          category="devices"
          items={deviceTypes}
          loading={loading}
          testId="env-donut-devices"
        />

        <EnvironmentDonutCard
          title="Web browsers"
          category="browsers"
          items={browsers}
          loading={loading}
          testId="env-donut-browsers"
        />

        <EnvironmentDonutCard
          title="Operating systems"
          category="operatingSystems"
          items={operatingSystems}
          loading={loading}
          testId="env-donut-os"
        />

        <EnvironmentDonutCard
          title="Locations"
          category="locations"
          items={locations}
          loading={loading}
          testId="env-donut-locations"
        />
      </div>
    </section>
  );
}

FlowEnvironmentPanel.propTypes = {
  filters: PropTypes.shape({
    organizationId: PropTypes.string,
    siteCollectionId: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    fromUtc: PropTypes.string,
    toUtc: PropTypes.string,
    refreshKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  title: PropTypes.string
};
